import { Stack, Text } from '@repletoryreach/sdk-ui';
import { formatInteger } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';

/**
 * How far through its recipient list a job is.
 *
 * The card view used to draw two bare `<div>`s, which a screen reader passes
 * over in silence, and the table view showed only "sent / total" as text, so
 * finding the stalled job in twenty-five rows meant doing twenty-five
 * divisions by eye. `UsageMeter` in sdk-ui is a 220px radial gauge and does
 * not fit a table cell, so this is a linear bar built from tokens, with the
 * progressbar role that carries the number to assistive tech.
 */
export function SendProgress({ sent, done, total, showLabel = true }: {
  sent: number;
  /**
   * Recipients no longer queued (4 October 2026). The bar used to be sent / total, so a job whose last recipients
   * failed or were left out never reached the end. The label still says how many were sent.
   */
  done?: number;
  total: number;
  showLabel?: boolean;
}) {
  const t = useT();
  const pct = total > 0 ? Math.min(100, Math.round(((done ?? sent) / total) * 100)) : 0;
  const label = t('broadcasts.progressDoneLabel', { sent: formatInteger(sent), total: formatInteger(total), pct });

  return (
    <Stack gap="xs" style={{ minWidth: 120 }}>
      {showLabel && <Text role="bodySmall">{label}</Text>}
      <div
        role="progressbar"
        aria-label={t('broadcasts.progressAria')}
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={label}
        style={{
          height: 4,
          borderRadius: 999,
          background: 'color-mix(in srgb, var(--rr-color-text-muted) 22%, transparent)',
          overflow: 'hidden'
        }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--rr-color-primary)' }} />
      </div>
    </Stack>
  );
}
